import type { CommerceClient, StoreWithOpeningHours } from '@altazion/commerce-sdk-core'
import type Handlebars from 'handlebars'

const DIALOG_SELECTOR = '[data-hz-store-picker]'
const TEMPLATE_ID = 'hz-store-picker-store-tmpl'
const RESULTS_SELECTOR = '[data-store-picker-results]'
const HINT_SELECTOR = '[data-store-picker-hint]'
const FORM_SELECTOR = '[data-store-picker-form]'
const INPUT_SELECTOR = '[data-store-picker-input]'

type HandlebarsRuntime = typeof Handlebars

/**
 * Initialise le comportement du dialog de choix de magasin.
 *
 * Écoute les actions :
 * - `open-store-picker`     — ouvre le dialog (showModal)
 * - `close-store-picker`    — ferme le dialog
 * - `store-picker-geolocate` — géolocalisation + recherche
 * - `select-store`          — associe le magasin à la session puis recharge la page
 *
 * La recherche par code postal est déclenchée par la soumission du formulaire
 * `[data-store-picker-form]`.
 *
 * Les résultats sont rendus via le template Handlebars contenu dans
 * `<template id="hz-store-picker-store-tmpl">`. Si Handlebars n'est pas fourni
 * ou que le template est absent, les magasins sont rendus avec un fallback HTML minimal.
 *
 * @returns Fonction de nettoyage (dispose) à appeler pour retirer les listeners.
 */
export function initStorePicker(client: CommerceClient, handlebars?: HandlebarsRuntime): () => void {
  if (typeof document === 'undefined') return () => { /* noop SSR */ }

  // ── Compilation du template Handlebars ──────────────────────────────────
  let renderStore: ((store: StoreWithOpeningHours) => string) | null = null

  const templateEl = document.getElementById(TEMPLATE_ID) as HTMLTemplateElement | null
  if (templateEl && handlebars) {
    try {
      const tplSource = templateEl.innerHTML
      const compiled = handlebars.compile(tplSource)
      renderStore = (store) => compiled(store)
    } catch {
      /* template invalide — fallback */
    }
  }

  if (!renderStore) {
    renderStore = fallbackRender
  }

  // ── Helpers ─────────────────────────────────────────────────────────────
  function getDialog(): HTMLDialogElement | null {
    return document.querySelector<HTMLDialogElement>(DIALOG_SELECTOR)
  }

  function openDialog(): void {
    getDialog()?.showModal()
  }

  function closeDialog(): void {
    getDialog()?.close()
  }

  function getConfig(): { countryCode: string; feature: string } {
    const dialog = getDialog()
    return {
      countryCode: dialog?.dataset.storePickerCountry ?? 'FRA',
      feature: dialog?.dataset.storePickerFeature ?? 'InStoreOrder',
    }
  }

  function renderResults(stores: StoreWithOpeningHours[]): void {
    const dialog = getDialog()
    if (!dialog) return

    const list = dialog.querySelector<HTMLElement>(RESULTS_SELECTOR)
    const hint = dialog.querySelector<HTMLElement>(HINT_SELECTOR)

    if (!list) return

    if (stores.length === 0) {
      list.innerHTML = ''
      list.hidden = true
      if (hint) {
        hint.textContent = 'Aucun magasin trouvé dans ce secteur.'
        hint.hidden = false
      }
      return
    }

    list.innerHTML = stores.map(s => renderStore!(s)).join('')
    list.hidden = false
    if (hint) hint.hidden = true
  }

  function showSearching(): void {
    const dialog = getDialog()
    if (!dialog) return
    const hint = dialog.querySelector<HTMLElement>(HINT_SELECTOR)
    const list = dialog.querySelector<HTMLElement>(RESULTS_SELECTOR)
    if (hint) { hint.textContent = 'Recherche en cours…'; hint.hidden = false }
    if (list) list.hidden = true
  }

  function showError(message: string): void {
    const dialog = getDialog()
    if (!dialog) return
    const hint = dialog.querySelector<HTMLElement>(HINT_SELECTOR)
    if (hint) { hint.textContent = message; hint.hidden = false }
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  async function handlePostalCodeSearch(postalCode: string): Promise<void> {
    showSearching()
    const { countryCode, feature } = getConfig()
    try {
      const stores = await client.stores.findByPostalCode(postalCode, countryCode, 10, feature)
      renderResults(stores)
    } catch {
      showError('La recherche a échoué. Vérifiez votre code postal et réessayez.')
    }
  }

  async function handleGeolocate(): Promise<void> {
    if (!navigator.geolocation) {
      showError('La géolocalisation n\'est pas disponible sur cet appareil.')
      return
    }
    showSearching()
    const { countryCode, feature } = getConfig()
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const stores = await client.stores.findByLocation(
            position.coords.latitude,
            position.coords.longitude,
            countryCode,
            10,
            feature
          )
          renderResults(stores)
        } catch {
          showError('La recherche a échoué. Veuillez réessayer.')
        }
      },
      () => {
        showError('Impossible d\'obtenir votre position. Vérifiez les autorisations de géolocalisation.')
      }
    )
  }

  async function handleSelectStore(storeGuid: string): Promise<void> {
    try {
      await client.session.associateStore(storeGuid)
      closeDialog()
      document.dispatchEvent(new CustomEvent('altazion:stores:store-associated', {
        bubbles: true,
        detail: { storeGuid },
      }))
      window.location.reload()
    } catch {
      showError('Impossible de sélectionner ce magasin. Veuillez réessayer.')
    }
  }

  // ── Listener global sur document ─────────────────────────────────────────
  function handleClick(event: Event): void {
    const target = (event.target as Element).closest('[data-action]') as HTMLElement | null
    if (!target) return

    const action = target.dataset.action
    switch (action) {
      case 'open-store-picker':
        openDialog()
        break
      case 'close-store-picker':
        closeDialog()
        break
      case 'store-picker-geolocate':
        void handleGeolocate()
        break
      case 'select-store': {
        const guid = target.dataset.storeGuid
        if (guid) void handleSelectStore(guid)
        break
      }
    }
  }

  function handleFormSubmit(event: Event): void {
    const form = (event.target as Element).closest(FORM_SELECTOR)
    if (!form) return
    event.preventDefault()
    const input = form.querySelector<HTMLInputElement>(INPUT_SELECTOR)
    const postalCode = input?.value.trim()
    if (postalCode) void handlePostalCodeSearch(postalCode)
  }

  document.addEventListener('click', handleClick)
  document.addEventListener('submit', handleFormSubmit)

  return () => {
    document.removeEventListener('click', handleClick)
    document.removeEventListener('submit', handleFormSubmit)
  }
}

function fallbackRender(store: StoreWithOpeningHours): string {
  const name = store.label ?? store.code ?? 'Magasin'
  const address = [store.streetAddress, store.postalCode, store.city].filter(Boolean).join(', ')
  return `<li class="hz-store-picker__item">` +
    `<div class="hz-store-picker__item-info">` +
    `<strong class="hz-store-picker__item-name">${escHtml(name)}</strong>` +
    (address ? `<address class="hz-store-picker__item-address">${escHtml(address)}</address>` : '') +
    `</div>` +
    `<button class="hz-store-picker__item-select" type="button" data-action="select-store" data-store-guid="${escHtml(store.guid)}">` +
    `Choisir ce magasin` +
    `</button>` +
    `</li>`
}

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
